/* ============================================================================
 *  CONEXIÓN SEMANAL — IGLESIA PUERTA DEL CIELO (IPDC)
 *  ---------------------------------------------------------------------------
 *  👉 ESTE ES EL ÚNICO ARCHIVO QUE DEBES EDITAR CADA SEMANA.
 *
 *  Aquí vive TODO el contenido que cambia (semana, verso, eventos, enlaces,
 *  imágenes). No necesitas tocar el diseño ni el código de los componentes.
 *
 *  CÓMO ACTUALIZAR:
 *   1. Cambia el texto entre comillas "".
 *   2. Cambia las fechas y horas de los eventos.
 *   3. Reemplaza los enlaces "#" por tus enlaces reales (Formspree, laiglesiapdc.com, etc.).
 *   4. Reemplaza las imágenes: sube tu foto a la carpeta /public/images/ y
 *      escribe la ruta, por ejemplo:  "/images/mi-foto.jpg"
 *      (o pega una URL completa que empiece con https://).
 *
 *  Lo que está entre [CORCHETES] es un marcador de posición que debes completar.
 * ==========================================================================*/

export const content = {
  // -------------------------------------------------------------------------
  // MARCA / GENERAL
  // -------------------------------------------------------------------------
  brand: {
    name: "Iglesia Puerta del Cielo",
    short: "IPDC",
    logo: "/ipdc-logo.png", // Logo blanco (PNG con fondo transparente)
  },

  // Aviso temporal en la parte inferior (déjalo vacío "" para ocultarlo).
  disclaimer: "",

  // Enlace al sitio web principal (aparece al final de la página).
  mainSite: { label: "Visita nuestro sitio web", href: "https://www.laiglesiapdc.com" },

  // -------------------------------------------------------------------------
  // 1. HERO  (la primera pantalla)
  // -------------------------------------------------------------------------
  hero: {
    titleTop: "CONEXIÓN",
    titleBottom: "SEMANAL",
    weekBadge: "SEMANA DE NOVIEMBRE 2", // 👈 CAMBIA ESTO CADA SEMANA
    // 👇 CAMBIA / AGREGA FOTOS DE FONDO AQUÍ.
    // panMode controla la animación del fondo cuando hay UNA sola foto:
    //   "reveal"   = recorrido vertical lento arriba→abajo (ideal fotos ALTAS/verticales).
    //   "kenburns" = leve acercamiento centrado (ideal fotos HORIZONTALES/apaisadas).
    // Si pones VARIAS fotos en bgImages, se funden en bucle (5s cada una).
    panMode: "kenburns",
    // layout: "photo" = fondo más claro y título más pequeño para que se vea la foto ("" = clásico).
    layout: "photo",
    // Fotos anteriores disponibles: /images/hero-ipdc-1..5.webp, /images/hero-nica-1..4.webp
    // Cada foto puede ser texto o { src, focus }. "focus" = "X% Y%" (en computadora mueve
    // la foto para que las caras queden debajo del título; 0% = parte de arriba de la foto).
    bgImages: [{ src: "/images/hero-iglesia-otono.webp", focus: "38% 22%" }],
    heroPhotoAlt: "Edificio de Iglesia Puerta del Cielo en otoño",
    scrollHint: "DESLIZA",
    // Píldora tocable debajo del título que lleva a la sección Gateway. Para mostrarla:
    // { badge: "Domingos 1 PM", label: "Gateway · En inglés", target: "gateway" }
    gatewayPill: null,
  },

  // -------------------------------------------------------------------------
  // 2. VERSO PARA EMPEZAR TU SEMANA
  // -------------------------------------------------------------------------
  // -------------------------------------------------------------------------
  // 🌎 MES DE LA HERENCIA HISPANA — tema "Un Solo Pueblo" (15 sep – 15 oct).
  //     Pon hidden: true para ocultar la sección. Edita el tema, versículo y texto.
  // -------------------------------------------------------------------------
  heritage: {
    hidden: true,
    eyebrow: "MES DE LA HERENCIA HISPANA",
    dateRange: "15 de Septiembre – 15 de Octubre",
    logo: "/images/unsolopueblo.png",
    logoAlt: "Un Solo Pueblo · Gálatas 3:28",
    theme: "Un Solo Pueblo",
    verse: {
      text: "Ya no hay judío ni griego; no hay esclavo ni libre; no hay varón ni mujer; porque todos vosotros sois uno en Cristo Jesús.",
      reference: "Gálatas 3:28",
    },
    body:
      "Somos una familia formada por muchas naciones, lenguas y culturas. Este mes celebramos con orgullo la riqueza de nuestra herencia hispana y recordamos que, por encima de toda bandera, en Cristo somos un solo pueblo. Celebremos de dónde venimos y, juntos, hacia dónde vamos.",
    // 🎬 VIDEO DEL MES. Reemplaza el archivo en /public/videos/ para cambiarlo.
    video: {
      label: "El video del mes",
      src: "/videos/un-solo-pueblo.mp4",
      poster: "/videos/un-solo-pueblo-poster.jpg",
    },
    // 🖼️ FLYERS DE EVENTOS (galería con lightbox: toca para ampliar). Edita/añade aquí.
    flyers: [
      {
        image: "/images/flyer-servicio-misionero.webp",
        title: "Servicio Misionero",
        subtitle: "Domingo 4 de Octubre · 8:00 AM · 10:15 AM · 1:00 PM",
      },
      {
        image: "/images/flyer-multicultural.webp",
        title: "Noche Multicultural",
        subtitle: "Viernes 9 de Octubre · 7:30 PM",
      },
    ],
    // 🌎 Banderas latinoamericanas para el borde de la sección (orden norte → sur).
    //    Edita/añade/quita países aquí (code = código del país; los SVG están en /public/images/flags/).
    flags: [
      { code: "mx", name: "México" },
      { code: "gt", name: "Guatemala" },
      { code: "sv", name: "El Salvador" },
      { code: "hn", name: "Honduras" },
      { code: "ni", name: "Nicaragua" },
      { code: "cr", name: "Costa Rica" },
      { code: "pa", name: "Panamá" },
      { code: "cu", name: "Cuba" },
      { code: "do", name: "República Dominicana" },
      { code: "pr", name: "Puerto Rico" },
      { code: "co", name: "Colombia" },
      { code: "ve", name: "Venezuela" },
      { code: "ec", name: "Ecuador" },
      { code: "pe", name: "Perú" },
      { code: "bo", name: "Bolivia" },
      { code: "br", name: "Brasil" },
      { code: "py", name: "Paraguay" },
      { code: "cl", name: "Chile" },
      { code: "ar", name: "Argentina" },
      { code: "uy", name: "Uruguay" },
    ],
  },

  // -------------------------------------------------------------------------
  // ✈️ RECUENTO DE MISIÓN (aparece justo debajo de Inicio). Pon hidden: true para ocultarlo.
  //    "slides" = fotos del carrusel (4:5, se ven continuas una tras otra).
  // -------------------------------------------------------------------------
  missionRecap: {
    hidden: true,
    eyebrow: "Viaje misionero · Nicaragua",
    title: "Esta misión fue de todos",
    places: ["Cinco Pinos", "Somotillo", "El Panal", "Masaya", "Chinandega"],
    paragraphs: [
      "Fuimos 14 de Puerta del Cielo a Nicaragua. Evangelizamos y llevamos medicamentos en Cinco Pinos, visitamos casas con canastas de comida en Somotillo, subimos la montaña hasta El Panal con ropa y mochilas para los niños.",
      "Gracias a todos los pastores que nos recibieron con brazos abiertos, y a nuestra iglesia por cada ofrenda y oración. Esta misión fue de todos.",
    ],
    quoteIntro: "Como dijo Pablo",
    quote: "Doy gracias a mi Dios siempre que me acuerdo de vosotros... por vuestra comunión en el evangelio.",
    quoteRef: "Filipenses 1:3, 5",
    swipeHint: "Desliza para ver más",
    slides: [
      { src: "/images/nica-1.webp", alt: "El equipo misionero de IPDC llegando a Nicaragua" },
      { src: "/images/nica-2.webp", alt: "Preparando ropa y canastas de comida para las familias" },
      { src: "/images/nica-3.webp", alt: "Orando por las familias y entregando ayuda casa por casa" },
      { src: "/images/nica-4.webp", alt: "Niños y familias recibiendo ayuda en las comunidades" },
      { src: "/images/nica-5.webp", alt: "Bienvenida al equipo misionero y paisajes de Nicaragua" },
    ],
  },

  verse: {
    eyebrow: "VERSO PARA EMPEZAR TU SEMANA",
    // 👇 CAMBIA EL VERSO AQUÍ. Dos versiones: RVR (español) y ESV (inglés).
    // "highlight" resalta una palabra clave (opcional). Edítalas cada semana.
    versions: [
      {
        id: "RVR",
        text: "Entrad por sus puertas con acción de gracias, por sus atrios con alabanza; alabadle, bendecid su nombre.",
        reference: "Salmo 100:4 (RVR1960)",
        highlight: "acción de gracias",
      },
      {
        id: "ESV",
        text: "Enter his gates with thanksgiving, and his courts with praise! Give thanks to him; bless his name!",
        reference: "Psalm 100:4 (ESV)",
        highlight: "thanksgiving",
      },
    ],
    // 📲 BOTÓN "COMPARTIR EN WHATSAPP": crea una imagen del verso (con logo) en el teléfono.
    //    "version" = qué versión va en la imagen ("RVR" o "ESV"). Pon hidden: true para ocultarlo.
    share: {
      hidden: false,
      version: "RVR",
      label: "Compartir en WhatsApp",
      workingLabel: "Preparando imagen…",
      downloadedNote: "Imagen descargada. Adjúntala en el chat de WhatsApp que se abrió.",
      errorNote: "No se pudo compartir. Intenta de nuevo.",
      footer: "Conexión Semanal · Iglesia Puerta del Cielo",
      bgImage: "/images/hero-worship.webp",
      fileName: "verso-de-la-semana-ipdc.png",
    },
  },

  // -------------------------------------------------------------------------
  // 🎪 CAMPAÑA FAMILIAR (evento especial con cuenta regresiva).
  //     Pon hidden: true para ocultar la sección.
  //     Edita el orador, las fechas y "startISO" (fecha y hora de inicio, hora local).
  // -------------------------------------------------------------------------
  campana: {
    hidden: true,
    eyebrow: "¡COMIENZA ESTA SEMANA!",
    title: "Campaña Familiar",
    speakerLabel: "Predicador invitado",
    speaker: "Dr. Samuel Otero",
    dateLabel: "24 – 27 de Septiembre",
    year: "2026",
    timeLabel: "",
    location: "Iglesia Puerta del Cielo · 14615 General Washington Dr, Woodbridge, VA",
    // ⏱️ Fecha y hora de inicio de la cuenta regresiva (hora local del visitante).
    //     Formato: "AÑO-MES-DÍAThora:min:seg" (24h). Ajusta la hora si es necesario.
    startISO: "2026-09-24T19:00:00",
    liveLabel: "¡La campaña ya comenzó!",
    // 📖 TEMA GENERAL + VERSÍCULO CLAVE (se muestran de forma destacada).
    themeLabel: "Tema General",
    generalTheme: "Transformando el Presente y Asegurando el Futuro de Nuestros Hogares",
    keyVerse: {
      text: "No las encubriremos a sus hijos, contando a la generación venidera las alabanzas de Jehová, y su potencia, y las maravillas que hizo.",
      reference: "Salmo 78:4",
    },
    // 🗓️ TEMAS POR DÍA (agenda de la campaña). Edita/añade/quita noches aquí.
    //    Campos por noche: day, dateNum, month, theme (obligatorios) + opcionales: tag, time, subtitle, verse.
    scheduleLabel: "Horario de la Campaña",
    schedule: [
      {
        day: "Jueves",
        dateNum: "24",
        month: "SEP",
        time: "7:00 PM",
        tag: "Noche de la Juventud",
        theme: "¿Cómo defender y sostener mi fe?",
      },
      {
        day: "Viernes",
        dateNum: "25",
        month: "SEP",
        time: "7:30 PM",
        tag: "Servicio para Todos",
        theme: "Pasando la Antorcha",
        verse: "Salmo 78:6-7",
      },
      {
        day: "Sábado",
        dateNum: "26",
        month: "SEP",
        time: "9:00 AM – 12:00 PM",
        tag: "Para Líderes de la Iglesia",
        theme: "Conferencia para Líderes",
      },
      {
        day: "Sábado",
        dateNum: "26",
        month: "SEP",
        time: "7:30 PM",
        tag: "Servicio para Todos",
        theme: "Corriendo la Carrera de Fe",
        subtitle: "Familias de Pacto",
        verse: "Hebreos 12:1-2",
      },
      {
        day: "Domingo",
        dateNum: "27",
        month: "SEP",
        time: "8:00 AM · 10:15 AM · 1:00 PM",
        tag: "Día de los Abuelos",
        theme: "Compartiendo la Mesa con la Próxima Generación",
        verse: "Salmo 145:4",
      },
    ],
    // Botón "Agregar al calendario" (Google Calendar, evento de todo el día 24–27 sep).
    calendarHref:
      "https://www.google.com/calendar/render?action=TEMPLATE&text=Campa%C3%B1a%20Familiar%20-%20Dr.%20Samuel%20Otero&dates=20260924/20260928&details=Campa%C3%B1a%20Familiar%20con%20el%20Dr.%20Samuel%20Otero%20en%20Iglesia%20Puerta%20del%20Cielo.&location=Iglesia%20Puerta%20del%20Cielo%2C%20Woodbridge%2C%20VA",
    bgImage: "/images/campana-otero.webp",
  },

  // -------------------------------------------------------------------------
  // AVISO ESPECIAL · HORARIO MODIFICADO  👈 EDITA/BORRA ESTO SEGÚN LA SEMANA
  // Deja specialHours en null para ocultar la sección por completo.
  // -------------------------------------------------------------------------
  specialHours: {
    eyebrow: "AVISO ESPECIAL",
    title: "Horario modificado este fin de semana",
    note: "Por nuestro Campamento Familiar (31 de julio – 2 de agosto), el horario de servicios cambia:",
    items: [
      {
        day: "Viernes",
        tag: "Cancelado",
        tone: "amber",
        icon: "CalendarX",
        text: "No habrá servicio este viernes.",
      },
      {
        day: "Sábado",
        tag: "Por confirmar",
        tone: "electric",
        icon: "Clock",
        text: "Reuniones Familiares: contacta a tu líder para conocer los posibles horarios modificados.",
      },
      {
        day: "Domingo",
        tag: "Cambio de horario",
        tone: "electric",
        icon: "Clock",
        text: "Un solo servicio, a las 10:15 AM.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 3. HORARIO SEMANAL
  // -------------------------------------------------------------------------
  schedule: {
    eyebrow: "HORARIO SEMANAL",
    intro:
      "Nuestra comunidad se fortalece a través de la oración, la enseñanza y la adoración. Cada semana, nos reunimos con un mismo propósito: crecer en fe y caminar juntos en comunión.",
    // Agrega o quita días libremente. Un horario puede ser texto ("8:00 AM") o un objeto
    // destacado { time, tag, link } (link = id de la sección a la que lleva al tocarlo).
    days: [
      {
        day: "Domingos",
        detail: "Servicios",
        times: ["8:00 AM", "10:15 AM", { time: "1:00 PM", tag: "Gateway · En inglés", link: "gateway" }],
      },
      { day: "Martes", detail: "Oración", times: ["7:30 PM"] },
      { day: "Viernes", detail: "Educación Cristiana", times: ["7:30 PM"] },
      { day: "Sábados", detail: "Reuniones Familiares", times: ["7:00 PM"] },
    ],
  },

  // -------------------------------------------------------------------------
  // ¿TE PERDISTE EL SERVICIO?  (grabaciones de los servicios recientes)
  // -------------------------------------------------------------------------
  services: {
    // 👇 PONLO EN true PARA OCULTAR TODA LA SECCIÓN "Revive este fin de semana" esta semana.
    //    Cuando vuelvas a tener videos que mostrar, cámbialo de nuevo a false.
    hidden: false,
    eyebrow: "¿TE PERDISTE EL SERVICIO?",
    title: "REVIVE ESTE FIN DE SEMANA",
    subtitle:
      "Vuelve a ver los servicios más recientes. Toca cualquier video para verlo en YouTube.",
    // Para cada video: pega el ENLACE de YouTube (o solo el ID) en "youtube".
    // Déjalo vacío ("") si el video aún no está disponible (mostrará "Disponible pronto").
    // La miniatura se toma de "thumbnail" (sube tu imagen a /public/images/ o usa una URL).
    // "tag" (opcional) resalta el video con una etiqueta azul.
    videos: [
      {
        title: "Gateway · Servicio en Inglés",
        date: "Domingo · 1:00 PM",
        youtube: "https://youtube.com/live/HjbO5KDT418",
        thumbnail: "",
        tag: "Gateway · En inglés",
      },
      {
        title: "Servicio Dominical",
        date: "Domingo · 10:15 AM",
        youtube: "https://youtube.com/live/Rtdyvugy0ts",
        thumbnail: "",
      },
    ],
  },

  // -------------------------------------------------------------------------
  // GATEWAY  (invitación al servicio en inglés de la 1:00 PM)
  // -------------------------------------------------------------------------
  gateway: {
    eyebrow: "IPDC GATEWAY · SERVICIO EN INGLÉS",
    title: "NOS VEMOS EN GATEWAY",
    time: "Todos los domingos · 1:00 PM",
    text:
      "Gateway es nuestro servicio en inglés: un ambiente vibrante para conectar, adorar y crecer en comunidad. Trae a un amigo, ¡te esperamos este domingo!",
    // Video vertical (9:16) que se reproduce en silencio; toca para oír el audio.
    video: "/videos/gateway.mp4",
    videoAudioHint: "Toca para oír el audio",
    // Poster que se funde con la foto (transición cada 4 segundos).
    image: "/images/gateway.webp",
    imageAlt: "Dos jóvenes sonrientes te invitan al servicio Gateway de la 1:00 PM",
    inviteImage: "/images/gateway-invite.webp",
    inviteImageAlt: "Invitación a Gateway: te esperamos cada domingo a la 1:00 PM",
    // Botones (icon: "MapPin" o "CalendarPlus"; variant: "primary" o "ghost").
    ctas: [
      {
        label: "Ven este domingo",
        icon: "MapPin",
        variant: "primary",
        href: "https://www.google.com/maps/dir/?api=1&destination=14615+General+Washington+Dr+Woodbridge+VA+22193",
      },
      {
        label: "Recordatorio semanal",
        icon: "CalendarPlus",
        variant: "ghost",
        href: "https://www.google.com/calendar/render?action=TEMPLATE&text=Gateway%20%C2%B7%20IPDC%20(Servicio%20en%20Ingl%C3%A9s)&dates=20261025T130000/20261025T143000&ctz=America/New_York&recur=RRULE:FREQ%3DWEEKLY;BYDAY%3DSU&details=Gateway%2C%20el%20servicio%20en%20ingl%C3%A9s%20de%20Iglesia%20Puerta%20del%20Cielo.%20Todos%20los%20domingos%20a%20la%201%3A00%20PM.&location=14615%20General%20Washington%20Dr%2C%20Woodbridge%2C%20VA%2022193",
      },
    ],
    // 📲 "INVITA A UN AMIGO": comparte la imagen de invitación + este texto por WhatsApp.
    invite: {
      hidden: false,
      label: "Invita a un amigo",
      workingLabel: "Preparando invitación…",
      downloadedNote: "Imagen descargada. Adjúntala en el chat de WhatsApp que se abrió.",
      errorNote: "No se pudo compartir. Intenta de nuevo.",
      image: "/images/gateway-invite-share.jpg",
      fileName: "invitacion-gateway-ipdc.jpg",
      text: "¡Te invito a Gateway, el servicio en inglés de Iglesia Puerta del Cielo! Todos los domingos a la 1:00 PM.\n14615 General Washington Dr, Woodbridge, VA 22193\nCómo llegar: https://www.google.com/maps/dir/?api=1&destination=14615+General+Washington+Dr+Woodbridge+VA+22193",
    },
  },

  // -------------------------------------------------------------------------
  // CLASES DE MEMBRESÍA   👈 EDITA LAS FECHAS AQUÍ (deja membership en null para ocultar)
  // -------------------------------------------------------------------------
  membership: {
    // 👇 PONLO EN true PARA OCULTAR TODA LA SECCIÓN "Clases de Membresía / Bautismo" (incluye el FAQ).
    hidden: true,
    eyebrow: "DA EL SIGUIENTE PASO",
    title: "Da el Siguiente Paso",
    intro:
      "Crece en tu caminar de fe con la familia IPDC. Este agosto abrimos dos series de clases: elige la que corresponde a tu próximo paso.",
    // 👇 DOS TIPOS DE CLASE, una al lado de la otra (Membresía y Bautismo).
    //    Edita el título, la descripción, el horario (schedulePill) y las fechas de cada una.
    //    "icon" acepta: "GraduationCap" o "Droplets".
    classes: [
      {
        id: "membresia",
        icon: "GraduationCap",
        title: "Clases de Membresía",
        text: "¿Listo para hacerte miembro de la familia IPDC? Acompáñanos durante el servicio de las 10:15 AM en nuestra próxima fecha de agosto.",
        schedulePill: "Domingos · 10:15 AM",
        dates: [
          { day: "30", month: "AGO", weekday: "Domingo" },
        ],
      },
      {
        id: "bautismo",
        icon: "Droplets",
        title: "Clases de Bautismo",
        text: "¿Deseas sellar tu fe a través del bautismo? Prepárate con nosotros en estas dos clases de los lunes antes de dar el paso.",
        schedulePill: "Lunes · 7:00 PM",
        dates: [
          { day: "24", month: "AGO", weekday: "Lunes" },
          { day: "31", month: "AGO", weekday: "Lunes" },
        ],
      },
    ],
    // 👇 PREGUNTAS FRECUENTES (acordeón desplegable).
    //    Usa **texto** para poner palabras en NEGRITA dentro de la respuesta.
    //    Si la respuesta necesita una lista, agrega "bullets": ["punto 1", "punto 2"].
    faqTitle: "Preguntas Frecuentes",
    faq: [
      {
        q: "¿Puedo asistir a la iglesia sin ser miembro?",
        a: "**Sí.** Todos son bienvenidos a asistir a nuestros servicios y actividades sin ser miembros. Sin embargo, la membresía le permite integrarse más profundamente en la iglesia y participar en oportunidades de servicio y liderazgo.",
      },
      {
        q: "¿Es necesario estar bautizado para ser miembro?",
        a: "**Sí**, el bautismo es un requisito para la membresía. Sin embargo, si aún no ha sido bautizado, puede inscribirse en nuestras Clases de Membresía, donde le guiaremos en este importante paso de fe.",
      },
      {
        q: "¿Qué sucede si falto a una clase?",
        a: "Se permite faltar solo a **una** clase durante el curso de membresía. Si faltas a más de una clase, **deberás reiniciar el curso en el siguiente período de clases.**",
      },
      {
        q: "¿Cuántas clases debo completar para ser miembro?",
        a: "Para completar el proceso de membresía, se deben tomar **4 clases.**",
      },
      {
        q: "¿Cuáles son los requisitos para ser miembro de Iglesia Puerta del Cielo?",
        a: "**Para convertirse en miembro de nuestra iglesia, debe cumplir con los siguientes requisitos:**",
        bullets: [
          "Tener al menos 12 años de edad.",
          "Haber asistido a Iglesia Puerta del Cielo por **un mínimo de 6 meses** antes de comenzar el curso. (Anteriormente, el requisito era de 3 meses.)",
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 4. ESTE MES  (carrusel de eventos)
  // -------------------------------------------------------------------------
  esteMes: {
    eyebrow: "ESTE MES",
    // Estas son las GRÁFICAS OFICIALES de los eventos del mes (imágenes verticales 4:5
    // que YA incluyen título, fecha y hora). Para actualizar: sube la nueva gráfica a
    // /public/images/ y cambia "image" (la ruta) y "alt" (descripción para accesibilidad).
    // 👇 CARRUSEL DE GRÁFICAS (flyers). Déjalo vacío [] si aún no hay gráficas del mes.
    // 🎞️ CARRUSEL "Lo que viene" (arriba del calendario). Posters 4:5. Edita/añade aquí.
    events: [
      { image: "/images/nov-asamblea.webp", title: "Asamblea Anual · Exclusivo para miembros" },
      { image: "/images/nov-matutino.webp", title: "Servicio Matutino · Ministerio de Damas" },
      { image: "/images/nov-jovenes.webp", title: "Servicio de Jóvenes · Noviembre" },
      { image: "/images/nov-accion-gracias.webp", title: "Servicio de Acción de Gracias" },
    ],
    // ⏳ CUENTAS REGRESIVAS (banners arriba de "Esta Semana"). Se muestran en orden de fecha.
    //    Cada una se oculta sola después de "endISO" (o pon hidden: true).
    //    Fechas con zona horaria (-05:00 = hora del Este en noviembre).
    countdowns: [
      {
        id: "asamblea",
        hidden: false,
        eyebrow: "Exclusivo para miembros de IPDC",
        title: "Asamblea Anual",
        note: "Se pide la asistencia de todos los miembros. ¡Tu presencia y tu voz son importantes para el futuro de nuestra iglesia!",
        dateLabel: "Sábado 7 de Noviembre",
        timeLabel: "7:00 PM",
        location: "IPDC Santuario · 14615 General Washington Dr, Woodbridge, VA",
        startISO: "2026-11-07T19:00:00-05:00",
        endISO: "2026-11-07T22:00:00-05:00",
        liveLabel: "¡La asamblea ya comenzó!",
        image: "/images/nov-asamblea.webp",
        imageAlt: "Asamblea Anual · Exclusivo para miembros de IPDC · Sábado 7 de noviembre · 7:00 PM",
        calendarLabel: "Agregar al calendario",
        calendarHref:
          "https://www.google.com/calendar/render?action=TEMPLATE&text=Asamblea%20Anual%20-%20IPDC%20(Miembros)&dates=20261107T190000/20261107T210000&ctz=America/New_York&details=Asamblea%20Anual%20exclusiva%20para%20miembros%20de%20Iglesia%20Puerta%20del%20Cielo.&location=IPDC%20Santuario%2C%2014615%20General%20Washington%20Dr%2C%20Woodbridge%2C%20VA%2022193",
      },
      {
        id: "accion-gracias",
        hidden: false,
        eyebrow: "Cuenta regresiva",
        title: "Servicio de Acción de Gracias",
        dateLabel: "Jueves 26 de Noviembre",
        timeLabel: "10:15 AM",
        location: "14615 General Washington Dr, Woodbridge, VA",
        startISO: "2026-11-26T10:15:00-05:00",
        endISO: "2026-11-26T13:00:00-05:00",
        liveLabel: "¡El servicio ya comenzó!",
        image: "/images/nov-accion-gracias.webp",
        imageAlt: "Servicio de Acción de Gracias · Jueves 26 de noviembre · 10:15 AM",
        calendarLabel: "Agregar al calendario",
        calendarHref:
          "https://www.google.com/calendar/render?action=TEMPLATE&text=Servicio%20de%20Acci%C3%B3n%20de%20Gracias%20-%20IPDC&dates=20261126T101500/20261126T121500&ctz=America/New_York&details=Servicio%20de%20Acci%C3%B3n%20de%20Gracias%20en%20Iglesia%20Puerta%20del%20Cielo.&location=14615%20General%20Washington%20Dr%2C%20Woodbridge%2C%20VA%2022193",
      },
    ],
    // 🗓️ ESTA SEMANA — se muestra ARRIBA del calendario. Edita cada semana.
    //    Campos: day (DOM/LUN/…), dateNum, month (opcional), title, time (opcional).
    //    Pon hidden: true o deja events: [] para ocultar toda la sección.
    thisWeek: {
      label: "Esta Semana",
      dateRange: "2 de Noviembre – 8 de Noviembre",
      events: [
        { day: "MAR", dateNum: "3", month: "NOV", title: "Servicio de Oración", time: "7:30 PM" },
        { day: "VIE", dateNum: "6", month: "NOV", title: "Educación Cristiana", time: "7:30 PM" },
        { day: "SÁB", dateNum: "7", month: "NOV", title: "Asamblea Anual · IPDC Santuario", time: "7:00 PM", tag: "Solo miembros", highlight: true },
        { day: "DOM", dateNum: "8", month: "NOV", title: "Servicios Dominicales", time: "8:00 AM · 10:15 AM" },
        // highlight: true resalta la tarjeta; tag = etiqueta; link = id de la sección a la que lleva al tocarla.
        { day: "DOM", dateNum: "8", month: "NOV", title: "Gateway · Servicio en Inglés", time: "1:00 PM", tag: "En inglés", highlight: true, link: "gateway" },
      ],
    },
    // 📅 CALENDARIO (interactivo, con flechas para cambiar de mes).
    //    Agrega un bloque por mes en "months". El calendario abre en el mes actual.
    calendar: {
      // Eventos: usa "day" (un día) o "days" [array] para eventos de varios días.
      // "replacesWeekly: true" oculta el servicio recurrente de ese día (ej. cuando un evento lo reemplaza).
      // "image" es opcional; si la pones, se muestra una miniatura que al tocarla
      // se amplía a pantalla completa (lightbox) en el panel del día.
      months: [
        {
          monthLabel: "Noviembre 2026",
          year: 2026,
          month: 11,
          events: [
            { day: 7, title: "Asamblea Anual · Exclusivo para miembros", time: "7:00 PM · IPDC Santuario", image: "/images/nov-asamblea.webp", replacesWeekly: true },
            { day: 15, title: "Servicio Matutino · Ministerio de Damas", time: "6:00 AM", image: "/images/nov-matutino.webp" },
            { day: 21, title: "Servicio de Jóvenes", time: "7:00 PM", image: "/images/nov-jovenes.webp" },
            { day: 26, title: "Servicio de Acción de Gracias", time: "10:15 AM", image: "/images/nov-accion-gracias.webp" },
          ],
        },
      ],
      // Servicios recurrentes por día de la semana (0=Dom, 1=Lun, 2=Mar, 3=Mié, 4=Jue, 5=Vie, 6=Sáb).
      weekly: [
        { weekday: 0, title: "Servicios Dominicales", time: "8:00 AM · 10:15 AM · 1:00 PM" },
        { weekday: 2, title: "Servicio de Oración", time: "7:30 PM" },
        { weekday: 5, title: "Educación Cristiana", time: "7:30 PM" },
        { weekday: 6, title: "Reuniones Familiares", time: "7:00 PM" },
      ],
    },
  },

  // -------------------------------------------------------------------------
  // 5. VENEZUELA EN NUESTROS CORAZONES
  // -------------------------------------------------------------------------
  venezuela: {
    eyebrow: "VENEZUELA EN NUESTROS CORAZONES",
    paragraph:
      "[ESCRIBE AQUÍ UN PÁRRAFO CORTO] Seguimos orando e intercediendo por Venezuela y por cada familia que sufre. Como iglesia, extendemos nuestras manos para servir, apoyar y llevar esperanza a quienes más lo necesitan.",
    image:
      "https://images.unsplash.com/photo-1693348227481-2467938e5db7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx2ZW5lenVlbGElMjB0ZXB1aSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODM5MDY2ODd8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Paisaje de Venezuela",
    cta: { label: "CÓMO AYUDAR", href: "#" },
  },

  // -------------------------------------------------------------------------
  // 6. CAMPAMENTO FAMILIAR ANUAL
  // -------------------------------------------------------------------------
  familyCamp: {
    eyebrow: "CAMPAMENTO FAMILIAR ANUAL",
    dates: "31 de julio al 2 de agosto",
    text:
      "Ya está abierta la inscripción para nuestro Campamento Familiar Anual en Christopher Run, Lake Anna. Regístrese abajo y viva un fin de semana de fe, descanso y comunión en familia.",
    image:
      "https://images.unsplash.com/photo-1444124818704-4d89a495bbae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxmYW1pbHklMjBjYW1waW5nJTIwdGVudCUyMHN1bnNldHxlbnwwfHx8fDE3ODM5MDY2ODd8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Campamento familiar junto al lago al atardecer",
    cta: { label: "REGÍSTRESE AQUÍ", href: "https://form.typeform.com/to/rKeUX2bM" },
  },

  // -------------------------------------------------------------------------
  // 6b. RETIRO DE JÓVENES  (IPDC Youth)   👈 EDITA AQUÍ CADA AÑO
  // -------------------------------------------------------------------------
  retiro: {
    logo: "/images/ipdc-youth-logo.png",
    // Foto de fondo tenue (opcional). Deja "" para solo fondo oscuro.
    bgImage: "/images/retiro-bg.webp",
    eyebrow: "IPDC YOUTH",
    title: "RETIRO DE JÓVENES",
    dates: "7, 8 y 9 de agosto de 2026",
    intro:
      "Tres días para desconectarte de la rutina y reconectarte con Dios. Un fin de semana de adoración, amistad y aventura en las montañas. ¡No te lo pierdas!",
    // Cada fila usa un ícono (MapPin, Bus, Clock, DollarSign, CalendarDays).
    details: [
      { icon: "MapPin", label: "Lugar", value: "Ridgecrest Conference Center · Black Mountain, NC" },
      { icon: "Bus", label: "Transporte", value: "Viajaremos en bus charter. La salida es a las 8:00 am, así que el punto de encuentro es el parqueo de IPDC a las 7:30 am." },
      { icon: "Clock", label: "Regreso", value: "Domingo, aproximadamente a las 9:00 pm." },
      { icon: "DollarSign", label: "Costo", value: "$120 por persona." },
    ],
    cta: { label: "REGÍSTRATE", href: "https://form.typeform.com/to/rfn4LKZP" },
  },

  // -------------------------------------------------------------------------
  // 7. CLAMANDO POR NUESTRA NACIÓN  (tema misionero del mes)
  // -------------------------------------------------------------------------
  nacion: {
    eyebrow: "JULIO 2026 · UNA NACIÓN BAJO DIOS",
    title: "CLAMANDO POR NUESTRA NACIÓN",
    // Video de fondo sutil (opcional). Sube tu .mp4 a /public/videos/ y pon la ruta aquí.
    bgVideo: "/videos/clamando-nacion.mp4",
    bgVideoWebm: "/videos/clamando-nacion.webm",
    scripture: "2 Crónicas 7:14",
    line: "Este año nuestra nación cumple 250 años de historia.",
    // Gráficas oficiales de la serie (imágenes verticales 4:5). Para actualizar,
    // sube la nueva imagen a /public/images/ y cambia "image" y "alt".
    gallery: [
      { image: "/images/nacion-1.png", alt: "Clamando por Nuestra Nación — Julio 2026, Una Nación Bajo Dios (2 Crónicas 7:14)" },
      { image: "/images/nacion-5b.png", alt: "Un Clamor: oremos por Sanidad, Familias, Líderes y La Iglesia" },
      { image: "/images/nacion-4.png", alt: "Nuestra Nación: esta es la tierra que Dios nos ha dado por hogar" },
      { image: "/images/nacion-3.png", alt: "250 años: este año nuestra nación cumple 250 años de historia" },
      { image: "/images/nacion-2.png", alt: "2 Crónicas 7:14 (RVR1960)" },
      { image: "/images/nacion-7.png", alt: "Servicio Misionero 'Clamando por Nuestra Nación' — 26 de agosto, 8 AM · 10:15 AM · 1 PM" },
      { image: "/images/nacion-6.png", alt: "Sirve Con Nosotros — 07/25 Alcance con The Life Church, 07/29 Empaque de mochilas y comida con PWCFF; registro en laiglesiapdc.com/misiones-verano-2026" },
    ],
  },

  // -------------------------------------------------------------------------
  // 8. PETICIÓN DE ORACIÓN
  // -------------------------------------------------------------------------
  prayer: {
    eyebrow: "ESTAMOS AQUÍ PARA TI",
    title: "¿NECESITAS ORACIÓN?",
    // Llamado destacado (déjalo vacío "" para ocultarlo).
    callout: "Acompáñanos todos los martes en nuestro Servicio de Oración a las 7:30 PM.",
    text:
      "Comparte tu petición con nosotros. Nuestro equipo de intercesión orará por ti esta semana.",
    // Fotos reales de la congregación en oración (collage). Reemplázalas en /public/images/.
    images: [
      { src: "/images/prayer-1.webp", alt: "Mujer en oración inclinada sobre la banca" },
      { src: "/images/prayer-2.webp", alt: "Mujer orando con las manos entrelazadas" },
      { src: "/images/prayer-3.webp", alt: "Congregación imponiendo manos y orando unidos" },
    ],
    cta: { label: "PETICIÓN DE ORACIÓN", href: "https://form.typeform.com/to/zA4kZHi2" },
  },

  // -------------------------------------------------------------------------
  // 9. ¡ÚNETE A NUESTRO MINISTERIO DE MISIONES!
  // -------------------------------------------------------------------------
  missionsMinistry: {
    eyebrow: "¡ÚNETE A NUESTRO MINISTERIO DE MISIONES!",
    // Imagen de fondo sutil (foto real). Reemplázala en /public/images/.
    bgImage: "/images/missions-1.webp",
    subtitle: "Servimos a través de:",
    // icon: usa uno de estos nombres -> "volunteer", "local", "evangelism", "global"
    cards: [
      { icon: "volunteer", label: "Voluntariado" },
      { icon: "local", label: "Alcance Local" },
      { icon: "evangelism", label: "Evangelismo" },
      { icon: "global", label: "Misiones Globales" },
    ],
    closing: "Sé parte de lo que Dios está haciendo para transformar vidas.",
    cta: { label: "ÚNETE AL LLAMADO", href: "https://form.typeform.com/to/kQKaYGxk" },
  },

  // -------------------------------------------------------------------------
  // 10. SIRVE CON IPDC MISSIONS ESTE VERANO  (Unidos Para Servir)
  // -------------------------------------------------------------------------
  summerServe: {
    // 👇 PONLO EN true PARA OCULTAR TODA LA SECCIÓN "Sirve este verano" (Unidos Para Servir).
    hidden: true,
    eyebrow: "UNIDOS PARA SERVIR",
    title: "SIRVE CON IPDC MISSIONS ESTE VERANO",
    intro:
      "Este verano nos unimos bajo nuestro tema de Misiones Locales 'Unidos para Servir', con cuatro oportunidades para servir a nuestra comunidad. ¡Llegó el momento de inscribirse! ¡Unidos para servir, vamos con todo! Lo esperamos con los brazos abiertos.",
    // Imagen de fondo sutil (foto real). Reemplázala en /public/images/.
    image: "/images/missions-2.webp",
    imageAlt: "Voluntarios de IPDC 'Unidos Para Servir'",
    events: [
      {
        date: "08/15",
        title: "EVENTO MASIVO DE REGRESO A CLASES",
        place: "Freedom High School",
        time: "11:00 AM – 2:00 PM",
      },
      {
        date: "08/22",
        title: "IPDC FESTIVAL",
        place: "Iglesia PDC",
        time: "10:00 AM – 2:00 PM",
      },
    ],
    cta: { label: "APÚNTATE A UNO O MÁS", href: "https://www.laiglesiapdc.com/misiones-verano-2026" },
  },

  // -------------------------------------------------------------------------
  // SUMMER FESTIVAL — IPDC FESTIVAL
  // -------------------------------------------------------------------------
  // 🎉 RESUMEN / AGRADECIMIENTO DEL FESTIVAL (se muestra DESPUÉS de la sección del Festival).
  //    Ponlo en hidden: true para ocultarlo. Edita el texto, las cifras y las fotos aquí.
  festivalRecap: {
    hidden: true,
    eyebrow: "FESTIVAL DE VERANO 2026",
    title: "¡Gracias!",
    text: "Gracias a todos los que nos acompañaron y a las más de 13 organizaciones que participaron. Juntos entregamos más de 400 mochilas escolares y recibimos a más de 600 personas a lo largo del día. ¡Unidos para servir, lo logramos!",
    stats: [
      { value: 13, suffix: "+", label: "Organizaciones" },
      { value: 400, suffix: "+", label: "Mochilas entregadas" },
      { value: 600, suffix: "+", label: "Asistentes" },
    ],
    images: [
      { src: "/images/recap-1.webp", alt: "Voluntarios de IPDC sirviendo comida en el Festival de Verano 2026" },
      { src: "/images/recap-2.webp", alt: "Mochilas escolares donadas en el Festival de Verano 2026" },
      { src: "/images/recap-3.webp", alt: "Photo booth del Festival de Verano 2026 de IPDC" },
      { src: "/images/recap-4.webp", alt: "Voluntarias en el stand de bienvenida de IPDC" },
      { src: "/images/recap-5.webp", alt: "Ministerio de Misiones cocinando en el Festival de Verano 2026" },
    ],
  },

  festival: {
    // 👇 PONLO EN true PARA OCULTAR TODA LA SECCIÓN del Festival (déjalo en false cuando vuelva el evento).
    hidden: true,
    eyebrow: "IPDC · EVENTO COMUNITARIO",
    title: "FESTIVAL DE VERANO",
    year: "2026",
    logo: "/images/festival-logo.png",
    // 👇 Insignia de urgencia (déjala "" para ocultarla). Cámbiala cada semana.
    whenBadge: "¡ES ESTE SÁBADO!",
    date: "Sábado 22 de agosto · 10 AM – 2 PM",
    place: "14615 General Washington Drive, Woodbridge, VA 22193",
    // Lo que encontrarás en el festival (se muestran como etiquetas).
    activities: [
      "Comida",
      "Recursos Comunitarios",
      "Juegos",
      "Comunidad",
      "Música",
      "Mochilas y Útiles Escolares para Niños",
    ],
    text:
      "Un día gratis para toda la familia: comida, música, juegos, recursos comunitarios y mochilas con útiles escolares para los niños. ¡Ven y celebra con nosotros!",
    // 🅿️ ESTACIONAMIENTO (déjalo en null para ocultarlo).
    parking: {
      note: "El estacionamiento de la iglesia será ÚNICAMENTE para nuestros invitados y visitantes.",
      overflowLabel: "Miembros: estacionen en el Overflow Lot",
      overflowAddress: "14602 Minnieville Rd, Woodbridge, VA 22193",
      overflowDistance: "A solo 1 cuadra de la iglesia",
      mapHref: "https://www.google.com/maps/search/?api=1&query=14602+Minnieville+Rd+Woodbridge+VA+22193",
    },
    // Video de fondo (silencio + bucle). Sube tu video a /public/videos/ y actualiza las rutas.
    videoMp4: "/videos/festival-web.mp4",
    videoWebm: "/videos/festival.webm",
    cta: { label: "MÁS INFORMACIÓN", href: "https://www.laiglesiapdc.com/festival2026" },
    // Barra de progreso de donaciones (interactiva). Actualiza "donated" y "goal" cada semana.
    donationBar: {
      label: "Mochilas escolares donadas",
      donated: 300,
      goal: 300,
      unit: "mochilas donadas",
      note: "¡Gracias! Alcanzamos el 100% de la meta.",
    },
  },

  // -------------------------------------------------------------------------
  // 11. COMITÉ DE ORIENTACIÓN LEGAL
  // -------------------------------------------------------------------------
  legal: {
    eyebrow: "COMITÉ DE ORIENTACIÓN LEGAL",
    paragraph:
      "A inicios del 2025, nuestra iglesia formó el Comité de Orientación Legal con el propósito de brindar orientación confiable a nuestra congregación en medio de los desafíos actuales relacionados con inmigración. En esta página encontrará consejos prácticos y conexiones con recursos legales y comunitarios confiables.",
    ctas: [
      { label: "MÁS INFO", href: "#", variant: "white" },
      { label: "¡QUIERO UNIRME!", href: "#", variant: "blue" },
    ],
    note: "Próximamente más información.",
  },

  // -------------------------------------------------------------------------
  // 12. NUESTROS MINISTERIOS
  // -------------------------------------------------------------------------
  ministerios: {
    title: "NUESTROS MINISTERIOS",
    text: "Descubre todos los ministerios donde puedes crecer, servir y pertenecer.",
    bgImage: "/images/ministerios-bg.webp",
    cta: { label: "NUESTROS MINISTERIOS", href: "https://www.laiglesiapdc.com/ministerios" },
    note: "Próximamente más información.",
  },

  // -------------------------------------------------------------------------
  // 13. PALABRA DEL PASTOR
  // -------------------------------------------------------------------------
  pastor: {
    eyebrow: "PALABRA DEL PASTOR",
    quote:
      "En IPDC, creemos en ser 'Un Hogar para la Comunidad', un lugar donde puedas adorar, acercarte a Dios y encontrar apoyo en una familia espiritual. Nos sentimos honrados de compartir contigo el amor de Dios, ya sea que nos visites por primera vez o que hayamos tenido el privilegio de encontrarnos contigo en la comunidad. Queremos que sepas que aquí siempre serás bienvenido.",
    name: "Luis Cartagena",
    role: "Pastor General",
    initials: "LC",
  },

  // -------------------------------------------------------------------------
  // 14. FOOTER
  // -------------------------------------------------------------------------
  footer: {
    address: "14615 General Washington Dr. Woodbridge, VA 22193", // [VERIFICAR CÓDIGO POSTAL]
    // icon: usa "instagram", "facebook" o "youtube"
    socials: [
      { icon: "instagram", label: "Instagram", href: "#" },
      { icon: "facebook", label: "Facebook", href: "#" },
      { icon: "youtube", label: "YouTube", href: "#" },
    ],
    cta: { label: "INSCRÍBETE", href: "#" },
    copyright: "© 2026 Iglesia Puerta del Cielo",
  },
};

export default content;
